import { useEffect } from 'react';

import { setMeta, usePublicSeo } from '../lib/seo';

/**
 * AnalyticsTags — inyección client-side de las etiquetas de SEM/analytics que el
 * Super Admin configura (GTM, GA4/gtag, Meta Pixel) + metas de verificación de
 * buscadores. Se monta UNA vez en la app (App.tsx).
 *
 * Todo es idempotente y best-effort:
 *   - sólo inyecta si el ID correspondiente NO está vacío;
 *   - no inyecta dos veces (marca cada nodo con data-seo-tag / id);
 *   - sólo corre en el navegador; nunca rompe si un ID está mal.
 *
 * Nota sobre verificación: la verificación fiable de Google Search Console / Bing
 * se hace vía GA/GTM o DNS, porque el fetch del verificador NO ejecuta JS y no
 * verá estas metas inyectadas por React. Las metas se agregan igual como
 * best-effort (algunos flujos las aceptan si el HTML ya las trae).
 */
export function AnalyticsTags() {
  const { data: config } = usePublicSeo();

  useEffect(() => {
    if (!config || typeof window === 'undefined') return;
    const a = config.analytics;

    if (a.gtm_id) injectGtm(a.gtm_id);
    if (a.ga4_id) injectGtag(a.ga4_id, a.google_ads_id);
    if (a.meta_pixel_id) injectMetaPixel(a.meta_pixel_id);

    // Metas de verificación (best-effort; ver nota del componente).
    setMeta('name', 'google-site-verification', config.verification.google ?? '');
    setMeta('name', 'msvalidate.01', config.verification.bing ?? '');
  }, [config]);

  return null;
}

/** True si ya inyectamos un tag con este id (evita duplicados). */
function has(id: string): boolean {
  return !!document.getElementById(id);
}

// --- Google Tag Manager ------------------------------------------------------
function injectGtm(id: string): void {
  if (has('tb-gtm')) return;
  const s = document.createElement('script');
  s.id = 'tb-gtm';
  s.dataset.seoTag = '1';
  s.text =
    "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});" +
    "var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;" +
    "j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})" +
    `(window,document,'script','dataLayer','${jsStr(id)}');`;
  document.head.appendChild(s);

  // <noscript><iframe> al principio del body (fallback sin JS).
  if (!has('tb-gtm-ns')) {
    const ns = document.createElement('noscript');
    ns.id = 'tb-gtm-ns';
    ns.dataset.seoTag = '1';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    ns.appendChild(iframe);
    document.body.insertBefore(ns, document.body.firstChild);
  }
}

// --- Google Analytics 4 (gtag.js) -------------------------------------------
function injectGtag(ga4Id: string, adsId: string): void {
  if (has('tb-gtag')) return;
  // Loader async del SDK de gtag.
  const loader = document.createElement('script');
  loader.id = 'tb-gtag';
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`;
  loader.dataset.seoTag = '1';
  document.head.appendChild(loader);

  // Init + config. Google Ads (AW-XXXX) comparte gtag, por eso un 2do config.
  const init = document.createElement('script');
  init.id = 'tb-gtag-init';
  init.dataset.seoTag = '1';
  let code =
    'window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}' +
    "gtag('js',new Date());" +
    `gtag('config','${jsStr(ga4Id)}');`;
  if (adsId) {
    code += `gtag('config','${jsStr(adsId)}');`;
  }
  init.text = code;
  document.head.appendChild(init);
}

// --- Meta (Facebook) Pixel ---------------------------------------------------
function injectMetaPixel(pixelId: string): void {
  if (has('tb-meta-pixel')) return;
  // Si ya hay un fbq (píxel hardcodeado en index.html), NO reinicializamos: evita
  // doble init/PageView. El campo del admin queda como alternativa si algún día se
  // quita el hardcode.
  if ((window as unknown as { fbq?: unknown }).fbq) return;
  const s = document.createElement('script');
  s.id = 'tb-meta-pixel';
  s.dataset.seoTag = '1';
  s.text =
    '!function(f,b,e,v,n,t,s)' +
    '{if(f.fbq)return;n=f.fbq=function(){n.callMethod?' +
    'n.callMethod.apply(n,arguments):n.queue.push(arguments)};' +
    "if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';" +
    'n.queue=[];t=b.createElement(e);t.async=!0;' +
    't.src=v;s=b.getElementsByTagName(e)[0];' +
    "s.parentNode.insertBefore(t,s)}(window,document,'script'," +
    "'https://connect.facebook.net/en_US/fbevents.js');" +
    `fbq('init','${jsStr(pixelId)}');fbq('track','PageView');`;
  document.head.appendChild(s);
}

/** Escape mínimo para incrustar un ID dentro de un string literal JS. */
function jsStr(v: string): string {
  return v.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/[\r\n]/g, '');
}
